import type { TurnBlock } from '../types.js';
import { trimEmptyLogEdges } from './agent-log-lines.js';

/**
 * True when a block's view renders nothing at all.
 *
 * Empty blocks are ordinary traffic, not corruption: Claude emits
 * signature-only `thinking` blocks that carry no text, a text
 * `content_block_start` arrives before its first delta, and a plan can land
 * with no entries. The views below already return `null` for these, but an
 * invisible block still holds a slot in the turn's block list — enough to cut
 * a run of tool calls in two (so the run renders uncollapsed with a wrong
 * "N more" count) and to open a fresh visual group mid-run. Turn layout has to
 * skip them for the same reason the views do, so both sides read the emptiness
 * rule from here.
 */
export function rendersNothing(block: TurnBlock): boolean {
  switch (block.type) {
    case 'text':
      return block.text.length === 0;
    case 'thinking':
      // An active thinking block renders the "Thinking…" shimmer even before
      // its first delta, so only a settled empty one is invisible.
      return block.text.length === 0 && !block.isActive;
    case 'plan':
      return block.entries.length === 0;
    case 'agent_log':
      return trimEmptyLogEdges(block.lines).length === 0;
    default:
      return false;
  }
}
